import logger from '../utils/logger';

/**
 * Google Calendar Service
 * Handles all Google Calendar operations through n8n webhook
 */

const GoogleCalendarService = {
  // Check if Google Calendar integration is enabled
  isEnabled: () => {
    return process.env.REACT_APP_GOOGLE_CALENDAR_ENABLED === 'true';
  },

  // Get n8n webhook URL
  getWebhookUrl: () => {
    return process.env.REACT_APP_N8N_WEBHOOK_URL || '';
  },

  // Create a Google Calendar event
  createEvent: async (meetingData, userTimezone = null) => {
    if (!GoogleCalendarService.isEnabled()) {
      throw new Error('Google Calendar integration is not enabled');
    }

    try {
      logger.log('🔄 Creating Google Calendar event for meeting:', meetingData);
      
      const formattedData = GoogleCalendarService.formatMeetingForGoogle(meetingData, userTimezone);
      
      if (!formattedData) {
        throw new Error('Failed to format meeting data for Google Calendar');
      }
      
      const payload = {
        operation: 'create',
        meetingId: meetingData.id,
        data: formattedData
      };
      
      logger.log('📤 Sending create event to webhook:', JSON.stringify(payload, null, 2));
      logger.log('🌐 Webhook URL:', GoogleCalendarService.getWebhookUrl());
      logger.log('🔍 Event Details Check:');
      logger.log('  - Title/Summary:', formattedData.summary);
      logger.log('  - Description:', formattedData.description);
      logger.log('  - Lead Email (Attendees):', formattedData.attendees?.map(a => a.email).join(', ') || 'None');
      logger.log('  - Start Time:', formattedData.start?.dateTime);
      logger.log('  - End Time:', formattedData.end?.dateTime);
      
      const response = await fetch(GoogleCalendarService.getWebhookUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      logger.log('📥 Webhook response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error('❌ Webhook error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      logger.log('✅ Webhook success response:', result);
      return result;
    } catch (error) {
      logger.error('❌ Error creating Google Calendar event:', error);
      throw error;
    }
  },

  // Update a Google Calendar event
  updateEvent: async (meetingData, googleEventId) => {
    if (!GoogleCalendarService.isEnabled()) {
      throw new Error('Google Calendar integration is not enabled');
    }

    try {
      const response = await fetch(GoogleCalendarService.getWebhookUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'update',
          meetingId: meetingData.id,
          googleEventId: googleEventId,
          data: GoogleCalendarService.formatMeetingForGoogle(meetingData)
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      logger.error('Error updating Google Calendar event:', error);
      throw error;
    }
  },

  // Delete a Google Calendar event
  deleteEvent: async (googleEventId) => {
    if (!GoogleCalendarService.isEnabled()) {
      throw new Error('Google Calendar integration is not enabled');
    }

    try {
      const response = await fetch(GoogleCalendarService.getWebhookUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'delete',
          googleEventId: googleEventId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      logger.error('Error deleting Google Calendar event:', error);
      throw error;
    }
  },

  // Fetch Google Calendar events
  fetchEvents: async (timeMin, timeMax) => {
    if (!GoogleCalendarService.isEnabled()) {
      return [];
    }

    try {
      const response = await fetch(GoogleCalendarService.getWebhookUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'list',
          timeMin: timeMin,
          timeMax: timeMax
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.events || [];
    } catch (error) {
      logger.error('Error fetching Google Calendar events:', error);
      return [];
    }
  },

  // Helper function to get timezone offset
  getTimezoneOffset(timezone, date) {
    try {
      // Simple approach: create date in target timezone and compare with UTC
      const formatter = new Intl.DateTimeFormat('en', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      const parts = formatter.formatToParts(date);
      const targetDate = new Date(
        parts.find(p => p.type === 'year').value,
        parts.find(p => p.type === 'month').value - 1,
        parts.find(p => p.type === 'day').value,
        parts.find(p => p.type === 'hour').value,
        parts.find(p => p.type === 'minute').value,
        parts.find(p => p.type === 'second').value
      );
      
      return date.getTime() - targetDate.getTime();
    } catch (error) {
      logger.warn('Error calculating timezone offset, using 0:', error);
      return 0;
    }
  },

  // Format meeting data for Google Calendar - NO DOUBLE CONVERSION
  formatMeetingForGoogle: (meetingData, userTimezone = null) => {
    // Handle both form data (date/time) and database data (meeting_date/meeting_time)
    const meetingDate = meetingData.meeting_date || meetingData.date;
    const meetingTime = meetingData.meeting_time || meetingData.time;
    const meetingType = meetingData.meeting_type || meetingData.type;
    
    if (!meetingDate || !meetingTime) {
      logger.error('Missing date or time in meeting data:', meetingData);
      return null;
    }

    // CRITICAL FIX: Use the already converted Helsinki time directly
    // The meetingDate and meetingTime are ALREADY in Helsinki timezone from booking conversion
    // DO NOT convert again - this was causing double conversion
    const timezone = 'Europe/Helsinki';
    
    // Parse the ALREADY CONVERTED Helsinki date and time
    const [year, month, day] = meetingDate.split('-').map(Number);
    const [hours, minutes] = meetingTime.split(':').map(Number);
    
    // FINAL FIX: Use the Helsinki time directly without any timezone adjustments
    // The meetingDate and meetingTime are ALREADY correctly converted to Helsinki timezone
    // Creating a simple Date object and letting Google Calendar handle the timezone
    const startDateTime = new Date(`${meetingDate}T${meetingTime}:00`);
    
    logger.log('🎯 SIMPLIFIED TIMEZONE HANDLING:', {
      inputDate: meetingDate,
      inputTime: meetingTime,
      createdDateTime: startDateTime.toISOString(),
      note: 'Using direct Helsinki time without complex adjustments'
    });
    
    // Create end datetime - add duration
    const durationMinutes = meetingData.duration || 60; // Default 60 minutes
    const endDateTime = new Date(startDateTime.getTime() + (durationMinutes * 60 * 1000));

    // Ensure we have valid datetime objects
    if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
      logger.error('Invalid datetime created from:', { meetingDate, meetingTime, year, month, day, hours, minutes });
      return null;
    }
    
    logger.log('✅ Google Calendar Event Creation - SIMPLIFIED:', {
      meetingDate: `${meetingDate} (Helsinki)`,
      meetingTime: `${meetingTime} (Helsinki)`,
      timezone,
      startDateTime: startDateTime.toISOString(),
      endDateTime: endDateTime.toISOString(),
      note: 'Direct Helsinki time usage - no complex adjustments'
    });
    
    logger.log('=== DATE/TIME PROCESSING DEBUG ===');
    logger.log('Original meeting data:', meetingData);
    logger.log('Extracted values:', {
      meetingDate,
      meetingTime,
      meetingType
    });
    logger.log('Date parsing:', {
      originalDate: meetingDate,
      originalTime: meetingTime,
      parsedComponents: { year, month: month - 1, day, hours, minutes },
      startDateTime: startDateTime.toISOString(),
      localDateString: startDateTime.toLocaleDateString(),
      timezone,
      approach: 'Direct Helsinki time - no adjustments'
    });
    logger.log('================================');

    // Format datetime for Google Calendar API with timezone
    // Use RFC3339 format with timezone specification
    const formatDateTimeForGoogle = (dateTime) => {
      const year = dateTime.getFullYear();
      const month = String(dateTime.getMonth() + 1).padStart(2, '0');
      const day = String(dateTime.getDate()).padStart(2, '0');
      const hours = String(dateTime.getHours()).padStart(2, '0');
      const minutes = String(dateTime.getMinutes()).padStart(2, '0');
      const seconds = String(dateTime.getSeconds()).padStart(2, '0');
      
      // Return datetime in ISO format without timezone offset
      // Google Calendar will use the separate timeZone field
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };

    // Create event summary - prioritize meeting title, then construct from type and contact
    let eventSummary = meetingData.title;
    if (!eventSummary || eventSummary.trim() === '') {
      const contactName = meetingData.contact_name || meetingData.name || 'Contact';
      eventSummary = `${meetingType || 'Meeting'} with ${contactName}`;
    }
    
    // Create event description with all relevant details
    let eventDescription = meetingData.description || meetingData.notes || '';
    if (!eventDescription || eventDescription.trim() === '') {
      eventDescription = `${meetingType || 'Meeting'} scheduled via CRM`;
      if (meetingData.contact_name) {
        eventDescription += ` with ${meetingData.contact_name}`;
      }
    }
    
    const eventData = {
      summary: eventSummary,
      description: eventDescription,
      start: {
        dateTime: formatDateTimeForGoogle(startDateTime),
        timeZone: timezone
      },
      end: {
        dateTime: formatDateTimeForGoogle(endDateTime),
        timeZone: timezone
      },
      attendees: [],
      location: meetingData.location || '',
      meeting_type: meetingType // Include meeting type for n8n workflow
    };

    // Add lead email as attendee (this is the key fix!)
    if (meetingData.lead_email) {
      eventData.attendees.push({ email: meetingData.lead_email });
    }

    // Add contact email if different from lead email
    if (meetingData.contact_email && meetingData.contact_email !== meetingData.lead_email) {
      eventData.attendees.push({ email: meetingData.contact_email });
    }

    // Add additional attendees if provided
    if (meetingData.attendees && Array.isArray(meetingData.attendees)) {
      meetingData.attendees.forEach(attendee => {
        if (attendee.email && !eventData.attendees.find(a => a.email === attendee.email)) {
          eventData.attendees.push({ email: attendee.email });
        }
      });
    }

    // Add video conference for online meetings
    if (meetingType === 'video' || meetingType === 'online') {
      eventData.conferenceData = {
        createRequest: {
          requestId: `crm-meeting-${meetingData.id}-${Date.now()}`,
          conferenceSolutionKey: {
            type: 'hangoutsMeet'
          }
        }
      };
    }

    logger.log('Formatted event data for Google Calendar:', eventData);
    return eventData;
  },

  // Get sync status display
  getSyncStatusDisplay: (status) => {
    const statusMap = {
      'synced': { emoji: '✅', text: 'Synced', color: 'text-green-600' },
      'syncing': { emoji: '⏳', text: 'Syncing', color: 'text-yellow-600' },
      'sync_failed': { emoji: '❌', text: 'Sync Failed', color: 'text-red-600' },
      'not_synced': { emoji: '📅', text: 'Not Synced', color: 'text-gray-600' }
    };

    return statusMap[status] || statusMap['not_synced'];
  },

  // Get Google Calendar events for a specific date (for availability checking) - OPTIMIZED
  getEventsForDate: async (date) => {
    if (!GoogleCalendarService.isEnabled()) {
      return [];
    }

    try {
      const dateString = date.toISOString().split('T')[0];
      
      // Create payload for n8n workflow to fetch events
      const payload = {
        operation: 'list',
        calendarId: 'primary',
        timeMin: `${dateString}T00:00:00Z`,
        timeMax: `${dateString}T23:59:59Z`,
        maxResults: 50,
        singleEvents: true,
        orderBy: 'startTime'
      };
      
      logger.log('📅 Fetching Google Calendar events for date:', dateString);
      
      // PERFORMANCE FIX: Add timeout and AbortController to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      try {
        const response = await fetch(GoogleCalendarService.getWebhookUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          signal: controller.signal // Add abort signal
        });
        
        clearTimeout(timeoutId); // Clear timeout if request completes
        
        if (!response.ok) {
          logger.warn('Failed to fetch Google Calendar events:', response.status);
          return [];
        }
        
        const result = await response.json();
        
        logger.log('✅ Google Calendar events fetched successfully:', result.events?.length || 0, 'events');
        
        // Return events or empty array if no events
        return result.events || [];
        
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          logger.warn('⏰ Google Calendar request timed out after 5 seconds - continuing without Google events');
        } else {
          logger.warn('🔌 Google Calendar fetch error:', fetchError.message);
        }
        return [];
      }
      
    } catch (error) {
      logger.warn('Error fetching Google Calendar events:', error);
      return [];
    }
  },

  // Format Google Calendar event for display
  formatGoogleEventForDisplay: (googleEvent) => {
    return {
      id: googleEvent.id,
      title: googleEvent.summary || 'Untitled Event',
      description: googleEvent.description || '',
      start: googleEvent.start?.dateTime || googleEvent.start?.date,
      end: googleEvent.end?.dateTime || googleEvent.end?.date,
      location: googleEvent.location || '',
      attendees: googleEvent.attendees?.map(a => a.email) || [],
      htmlLink: googleEvent.htmlLink,
      hangoutLink: googleEvent.hangoutLink,
      source: 'google',
      color: '#34A853' // Google green
    };
  }
};

export default GoogleCalendarService;